/**
 * A character somebody can turn around and look at.
 *
 * The server draws the whole body already, from one fixed corner, and that
 * picture is what the page arrives with. It is a photograph though: it shows
 * the front and one side, and a skin is a thing people paint on all six.
 *
 * So where the browser can, the photograph is replaced by the model it is a
 * photograph of - six boxes of six faces each, cut out of the same sheet the
 * server drew from, standing in a space the reader can turn with a finger.
 * Nothing is downloaded for it: the sheet is the picture the skin already is,
 * and the faces are that one picture positioned six-and-thirty ways.
 *
 * The photograph stays in the markup until the model is standing, so a page
 * whose script never arrives shows a character rather than a hole, and one
 * whose sheet fails to load keeps the picture it already had.
 */

import { query, queryAll } from "../core/dom";

/** Marks the box a character stands in. */
export const VIEWER = "data-skin-viewer";

/** Which square of the sheet is which face of which part.
 *
 * The two shapes a skin has put the same head in the same place, so this is
 * written once for the newer one and the older one borrows what it lacks.
 * Every rectangle is `[x, y, width, height]` in the sheet's own pixels.
 */
const PARTS = {
    // `size` is [width, height, depth] and `at` is where the middle of the box
    // sits, both in sheet pixels, measured from the middle of the character -
    // which is what it turns around, so it is what everything is measured
    // from. A character is thirty-two pixels tall, so its waist is at sixteen
    // and the top of its head is twelve above the middle.
    head: { size: [8, 8, 8], at: [0, 12, 0] },
    body: { size: [8, 12, 4], at: [0, 2, 0] },
    right_arm: { size: [4, 12, 4], at: [-6, 2, 0] },
    left_arm: { size: [4, 12, 4], at: [6, 2, 0] },
    right_leg: { size: [4, 12, 4], at: [-2, -10, 0] },
    left_leg: { size: [4, 12, 4], at: [2, -10, 0] },
} as const;

type Face = "front" | "back" | "left" | "right" | "top" | "bottom";
type Rect = readonly [number, number, number, number];

/**
 * The picture a model is cut out of, and how tall it is.
 *
 * The height travels with the address because the two shapes of skin are
 * sixty-four wide and only one of them is sixty-four tall. Telling the
 * browser to draw the older one at the newer one's size stretches it to
 * twice its height, and every face then reads from rows which are not the
 * ones it is named after - which does not fail anywhere, it just quietly
 * draws somebody else's elbow onto a forehead.
 */
interface Sheet {
    /** Where the picture is. */
    url: string;

    /** How tall the picture really is, in its own pixels. */
    height: number;
}

/** The six faces of every box, on the newer sheet. */
const UV: Record<string, Record<"base" | "over", Record<Face, Rect>>> = {
    head: {
        base: {
            top: [8, 0, 8, 8],
            bottom: [16, 0, 8, 8],
            right: [0, 8, 8, 8],
            front: [8, 8, 8, 8],
            left: [16, 8, 8, 8],
            back: [24, 8, 8, 8],
        },
        over: {
            top: [40, 0, 8, 8],
            bottom: [48, 0, 8, 8],
            right: [32, 8, 8, 8],
            front: [40, 8, 8, 8],
            left: [48, 8, 8, 8],
            back: [56, 8, 8, 8],
        },
    },
    body: {
        base: {
            top: [20, 16, 8, 4],
            bottom: [28, 16, 8, 4],
            right: [16, 20, 4, 12],
            front: [20, 20, 8, 12],
            left: [28, 20, 4, 12],
            back: [32, 20, 8, 12],
        },
        over: {
            top: [20, 32, 8, 4],
            bottom: [28, 32, 8, 4],
            right: [16, 36, 4, 12],
            front: [20, 36, 8, 12],
            left: [28, 36, 4, 12],
            back: [32, 36, 8, 12],
        },
    },
    right_arm: {
        base: {
            top: [44, 16, 4, 4],
            bottom: [48, 16, 4, 4],
            right: [40, 20, 4, 12],
            front: [44, 20, 4, 12],
            left: [48, 20, 4, 12],
            back: [52, 20, 4, 12],
        },
        over: {
            top: [44, 32, 4, 4],
            bottom: [48, 32, 4, 4],
            right: [40, 36, 4, 12],
            front: [44, 36, 4, 12],
            left: [48, 36, 4, 12],
            back: [52, 36, 4, 12],
        },
    },
    left_arm: {
        base: {
            top: [36, 48, 4, 4],
            bottom: [40, 48, 4, 4],
            right: [32, 52, 4, 12],
            front: [36, 52, 4, 12],
            left: [40, 52, 4, 12],
            back: [44, 52, 4, 12],
        },
        over: {
            top: [52, 48, 4, 4],
            bottom: [56, 48, 4, 4],
            right: [48, 52, 4, 12],
            front: [52, 52, 4, 12],
            left: [56, 52, 4, 12],
            back: [60, 52, 4, 12],
        },
    },
    right_leg: {
        base: {
            top: [4, 16, 4, 4],
            bottom: [8, 16, 4, 4],
            right: [0, 20, 4, 12],
            front: [4, 20, 4, 12],
            left: [8, 20, 4, 12],
            back: [12, 20, 4, 12],
        },
        over: {
            top: [4, 32, 4, 4],
            bottom: [8, 32, 4, 4],
            right: [0, 36, 4, 12],
            front: [4, 36, 4, 12],
            left: [8, 36, 4, 12],
            back: [12, 36, 4, 12],
        },
    },
    left_leg: {
        base: {
            top: [20, 48, 4, 4],
            bottom: [24, 48, 4, 4],
            right: [16, 52, 4, 12],
            front: [20, 52, 4, 12],
            left: [24, 52, 4, 12],
            back: [28, 52, 4, 12],
        },
        over: {
            top: [4, 48, 4, 4],
            bottom: [8, 48, 4, 4],
            right: [0, 52, 4, 12],
            front: [4, 52, 4, 12],
            left: [8, 52, 4, 12],
            back: [12, 52, 4, 12],
        },
    },
};

/**
 * Where the left limbs come from on a sheet which has none.
 *
 * A reflection swaps the two sides of a limb, so the outer side of the left
 * arm is the outer side of the right one - which is the rectangle the right
 * arm calls its right, not its left. Taking the near one instead would wrap
 * the outside of one arm around the inside of the other.
 */
const MIRRORS: Record<string, Record<Face, Rect>> = {
    left_arm: {
        top: UV.right_arm!.base.top,
        bottom: UV.right_arm!.base.bottom,
        right: UV.right_arm!.base.left,
        left: UV.right_arm!.base.right,
        front: UV.right_arm!.base.front,
        back: UV.right_arm!.base.back,
    },
    left_leg: {
        top: UV.right_leg!.base.top,
        bottom: UV.right_leg!.base.bottom,
        right: UV.right_leg!.base.left,
        left: UV.right_leg!.base.right,
        front: UV.right_leg!.base.front,
        back: UV.right_leg!.base.back,
    },
};

/** How much of the box the layer worn over it stands out by, in sheet pixels. */
const PUFF = 0.6;

/** Where the character is turned to before anybody touches it.
 *
 * The same corner the server drew its picture from, so the model appears
 * where the photograph it replaces was standing rather than spinning into
 * place the moment the page settles.
 */
const START = { yaw: -32, pitch: -14 };

/** How far a drag turns the character, in degrees per pixel. */
const TURN = { yaw: 0.55, pitch: 0.35 };

/** How far the character may be tipped, so it is never seen from underneath. */
const PITCH_LIMIT = 34;

/** Return the transform which puts one face on one side of a box. */
function faceTransform(face: Face, size: readonly number[], unit: number): string {
    const [width = 0, height = 0, depth = 0] = size;
    const half = (value: number) => `${((value / 2) * unit).toFixed(2)}px`;

    // Every face is hung on the point its box stands at, so it is pulled back
    // by half of itself first and only then pushed out to its own side.
    const middle = "translate(-50%, -50%)";

    switch (face) {
        case "front":
            return `${middle} translateZ(${half(depth)})`;
        case "back":
            return `${middle} translateZ(-${half(depth)}) rotateY(180deg)`;
        // The character's own left, which is on the reader's right when the
        // two of them are looking at each other.
        case "left":
            return `${middle} translateX(${half(width)}) rotateY(90deg)`;
        case "right":
            return `${middle} translateX(-${half(width)}) rotateY(-90deg)`;
        case "top":
            return `${middle} translateY(-${half(height)}) rotateX(90deg)`;
        default:
            return `${middle} translateY(${half(height)}) rotateX(-90deg)`;
    }
}

/** Return how wide and tall one face is, in sheet pixels. */
function faceSize(face: Face, size: readonly number[]): [number, number] {
    const [width = 0, height = 0, depth = 0] = size;

    if (face === "top" || face === "bottom") {
        return [width, depth];
    }

    if (face === "left" || face === "right") {
        return [depth, height];
    }

    return [width, height];
}

/** Build one face of one box. */
function buildFace(
    face: Face,
    rect: Rect,
    size: readonly number[],
    unit: number,
    sheet: Sheet,
    mirrored: boolean,
): HTMLElement {
    const [across, down] = faceSize(face, size);
    const element = document.createElement("i");
    element.className = "skin-model__face";

    element.style.width = `${(across * unit).toFixed(2)}px`;
    element.style.height = `${(down * unit).toFixed(2)}px`;
    // Reflecting a limb turns every one of its faces around as well, or a
    // shoulder patch would cross from the outside of one arm to the inside
    // of the other.
    element.style.transform = faceTransform(face, size, unit) + (mirrored ? " scaleX(-1)" : "");
    element.style.backgroundImage = `url("${sheet.url}")`;
    element.style.backgroundSize = `${(64 * unit).toFixed(2)}px ${(sheet.height * unit).toFixed(2)}px`;
    element.style.backgroundPosition = `${(-rect[0] * unit).toFixed(2)}px ${(-rect[1] * unit).toFixed(2)}px`;

    return element;
}

/** Build one box of the body, or the layer worn over it. */
function buildBox(
    rects: Record<Face, Rect>,
    size: readonly number[],
    at: readonly number[],
    unit: number,
    sheet: Sheet,
    mirrored = false,
): HTMLElement {
    const box = document.createElement("div");
    box.className = "skin-model__box";

    const [x = 0, y = 0, z = 0] = at;
    // The room is measured upwards and the browser measures downwards, which
    // is the whole of the difference between the two.
    box.style.transform = `translate3d(${(x * unit).toFixed(2)}px, ${(-y * unit).toFixed(2)}px, ${(z * unit).toFixed(2)}px)`;

    for (const face of ["front", "back", "left", "right", "top", "bottom"] as Face[]) {
        box.append(buildFace(face, rects[face], size, unit, sheet, mirrored));
    }

    return box;
}

/** Return whether the bottom half of `sheet` was never drawn on. */
function isLegacy(sheet: HTMLImageElement): boolean {
    if (sheet.naturalHeight < 64) {
        return true;
    }

    // A sheet somebody padded out to the newer shape in a picture editor has
    // a height which says it has two more limbs and nothing drawn where they
    // would be, so the pixels are asked rather than the measurements.
    try {
        const canvas = document.createElement("canvas");
        canvas.width = 64;
        canvas.height = 16;

        const paper = canvas.getContext("2d", { willReadFrequently: true });
        if (!paper) {
            return false;
        }

        paper.drawImage(sheet, 0, 48, 64, 16, 0, 0, 64, 16);
        const pixels = paper.getImageData(0, 0, 64, 16).data;

        for (let at = 3; at < pixels.length; at += 4) {
            if (pixels[at]) {
                return false;
            }
        }

        return true;
    } catch {
        // A sheet the browser will not let us read is a sheet we do not get
        // to be clever about; it is drawn as the newer shape it claims to be.
        return false;
    }
}

/** Build the whole character out of `sheet`. */
function buildModel(sheet: Sheet, unit: number, legacy: boolean): HTMLElement {
    const model = document.createElement("div");
    model.className = "skin-model";

    for (const [name, part] of Object.entries(PARTS)) {
        const rects = UV[name];
        if (!rects) {
            continue;
        }

        const mirrored = legacy ? MIRRORS[name] : undefined;
        model.append(buildBox(mirrored ?? rects.base, part.size, part.at, unit, sheet, mirrored !== undefined));

        // The older sheet carries a hat and nothing else worn over anything.
        if (legacy && name !== "head") {
            continue;
        }

        const puffed = part.size.map((edge) => edge + PUFF);
        model.append(buildBox(rects.over, puffed, part.at, unit, sheet));
    }

    return model;
}

/** Let a finger, a mouse or the arrow keys turn one character around. */
function bindTurning(stage: HTMLElement, model: HTMLElement): void {
    let yaw = START.yaw;
    let pitch = START.pitch;
    let holding = 0;
    let from = { x: 0, y: 0 };

    const settle = () => {
        model.style.transform = `rotateX(${pitch.toFixed(1)}deg) rotateY(${yaw.toFixed(1)}deg)`;
    };

    const turn = (byX: number, byY: number) => {
        yaw += byX * TURN.yaw;
        pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch + byY * TURN.pitch));
        settle();
    };

    settle();

    stage.addEventListener("pointerdown", (event: PointerEvent) => {
        holding = event.pointerId;
        from = { x: event.clientX, y: event.clientY };
        stage.setPointerCapture(event.pointerId);
        stage.classList.add("is-turning");
    });

    stage.addEventListener("pointermove", (event: PointerEvent) => {
        if (event.pointerId !== holding || !stage.classList.contains("is-turning")) {
            return;
        }

        turn(event.clientX - from.x, -(event.clientY - from.y));
        from = { x: event.clientX, y: event.clientY };
        event.preventDefault();
    });

    const release = (event: PointerEvent) => {
        if (event.pointerId !== holding) {
            return;
        }

        holding = 0;
        stage.classList.remove("is-turning");
    };

    stage.addEventListener("pointerup", release);
    stage.addEventListener("pointercancel", release);

    // Turning a character around with a keyboard is the same gesture in
    // steps, and it is the only one somebody who cannot hold a pointer has.
    stage.addEventListener("keydown", (event: KeyboardEvent) => {
        const steps: Record<string, [number, number]> = {
            ArrowLeft: [-24, 0],
            ArrowRight: [24, 0],
            ArrowUp: [0, 12],
            ArrowDown: [0, -12],
        };

        const step = steps[event.key];
        if (!step) {
            return;
        }

        turn(step[0], step[1]);
        event.preventDefault();
    });

    // Somebody who has turned the character upside down wants it back, and
    // reloading the page is not the way to ask for that.
    stage.addEventListener("dblclick", () => {
        yaw = START.yaw;
        pitch = START.pitch;
        settle();
    });
}

/** Stand one character up in the box it was drawn in. */
function raise(stage: HTMLElement): void {
    const address = stage.getAttribute("data-skin-sheet");
    if (!address) {
        return;
    }

    const photograph = query<HTMLImageElement>(".skin-body", stage);
    const paper = new Image();

    paper.addEventListener("load", () => {
        // A character is thirty-two pixels from its feet to the top of its
        // head, and it is turned around inside the box rather than beside it,
        // so the box has to hold its longest diagonal rather than its height.
        const unit = Math.max(1, Math.floor(stage.clientHeight / 42));
        const sheet: Sheet = { url: address, height: paper.naturalHeight || 64 };
        const model = buildModel(sheet, unit, isLegacy(paper));

        stage.append(model);
        stage.classList.add("is-standing");

        // Telling somebody they can turn a picture around is only true once
        // there is a model rather than a picture, so the line is written into
        // the page hidden and shown from here.
        const hint = stage.closest(".panel")?.querySelector<HTMLElement>("[data-skin-turn-hint]");
        if (hint) {
            hint.hidden = false;
        }

        if (photograph) {
            photograph.hidden = true;
        }

        bindTurning(stage, model);
    });

    paper.src = address;
}

/** Stand up every character of the page which asked to be stood up. */
export function initSkinViewers(): void {
    if (!CSS.supports("transform-style", "preserve-3d")) {
        return;
    }

    for (const stage of queryAll<HTMLElement>(`[${VIEWER}]`)) {
        raise(stage);
    }
}
